import {isFeatureEnabled} from 'feature-flags';
import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  openDialog,
  Separator,
} from 'platform/components';
import {Box, HStack, Show, Space, VStack} from 'platform/foundation';

import {useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {useListThemeQuery} from '@dms/api/core';
import {
  GetSalePresetSettingsV2ResponseBody,
  GetSalePresetQualitySealImpactResponseBody,
  useCreateSalePresetSettingsV2Mutation,
  useGetBackgroundRemovalListQuery,
  useGetWatermarkListQuery,
  useListWarrantyProgramQuery,
  useLazyGetSalePresetQualitySealImpactQuery,
  useUpdateSalePresetSettingsV2Mutation,
} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError, MakeSelect, useIsQualitySealVisible} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {openRepublishInfoDialog} from '../../../components/RepublishInfoDialogContent/openRepublishInfoDialog';
import {salePresetFormSchema, SalePresetFormValues} from '../types/SalePresetFormValues';
import {QualitySealFormField} from './QualitySealFormField';
import {SetNewVehicleDialog} from './SetNewVehicleDialog';

type SalePresetFormProps = {
  settings?: GetSalePresetSettingsV2ResponseBody;
};

export function SalePresetForm(props: SalePresetFormProps) {
  const {id} = useParams();
  const navigate = useNavigate();

  const isSalesWarrantyEnabled = isFeatureEnabled(featureFlags.SALES_WARRANTY);
  const isQualitySealVisible = useIsQualitySealVisible();

  // The make select reports only the new selection, so the previously selected makes are kept here
  // to tell a widening change from a narrowing one.
  const selectedMakesRef = useRef<string[]>(props.settings?.makes ?? []);

  // Seal choices are a union over the selected makes, so adding a make can only widen the offer and
  // never invalidates the current default. Only a removed make can, and clearing on every change
  // would silently drop (and propagate the removal of) a default the user never touched.
  const onChangeMakes = (
    value: string | number | string[] | null,
    formApi: UseFormReturn<SalePresetFormValues>
  ) => {
    const nextMakes = Array.isArray(value) ? value.map(String) : [];
    const wasMakeRemoved = selectedMakesRef.current.some((make) => !nextMakes.includes(make));
    selectedMakesRef.current = nextMakes;

    if (!wasMakeRemoved) {
      return;
    }

    formApi.setValue('defaultQualitySealKey', null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const {data: watermarkListData, isLoading: isWatermarkQueryLoading} = useGetWatermarkListQuery();
  const {data: backgroundRemovalListData, isLoading: isBackgroundRemovalQueryLoading} =
    useGetBackgroundRemovalListQuery();
  const {data: themeListData, isLoading: isListThemeQueryLoading} = useListThemeQuery();
  const {data: warrantyProgramListData, isLoading: isWarrantyProgramQueryLoading} =
    useListWarrantyProgramQuery(undefined, {skip: !isSalesWarrantyEnabled});
  const [createSalePreset, {isLoading: isCreatingSalePreset}] =
    useCreateSalePresetSettingsV2Mutation();
  const [updateSalePreset, {isLoading: isUpdatingSalePreset}] =
    useUpdateSalePresetSettingsV2Mutation();
  const [getQualitySealImpact, {isFetching: isQualitySealImpactLoading}] =
    useLazyGetSalePresetQualitySealImpactQuery();

  const isLoading =
    isCreatingSalePreset ||
    isUpdatingSalePreset ||
    isWatermarkQueryLoading ||
    isBackgroundRemovalQueryLoading ||
    isListThemeQueryLoading ||
    isWarrantyProgramQueryLoading ||
    isQualitySealImpactLoading;

  const watermarkListOptions = watermarkListData?.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const backgroundRemovalOptions = backgroundRemovalListData?.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const themeListOptions = themeListData?.map((item) => ({
    value: item.themeId,
    label: item.name,
  }));

  const warrantyProgramOptions = warrantyProgramListData?.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const createOrUpdateSalePreset = async (
    values: SalePresetFormValues,
    action: 'confirm' | 'cancel',
    canPropagateQualitySeal: boolean
  ) => {
    const applyToVehicles = action === 'confirm';

    const dataToSend = {
      name: values.name,
      makes: values.makes,
      watermarkId: values.watermarkId,
      backgroundRemovalId: values.backgroundRemovalId,
      promotionalPhotoId: null,
      digitalCertificateThemeId: values.digitalCertificateThemeId,
      applyToVehicles,
      ...(isSalesWarrantyEnabled && {warrantyProgramId: values.warrantyProgramId}),
      ...(isQualitySealVisible && {
        defaultQualitySealKey: values.defaultQualitySealKey,
        propagateQualitySeal: action === 'confirm' && canPropagateQualitySeal,
      }),
    };
    if (id) {
      await updateSalePreset({
        salePresetId: id,
        updateSalePresetSettingsV2RequestBody: dataToSend,
      })
        .unwrap()
        .then(() => {
          if (action === 'confirm') {
            openRepublishInfoDialog({
              onCloseComplete: () => {
                navigate(settingsRoutes.salePreset);
              },
            });
            return;
          }

          navigate(settingsRoutes.salePreset);
        })
        .catch(handleApiError);
    } else {
      await createSalePreset({
        createSalePresetSettingsV2RequestBody: dataToSend,
      })
        .unwrap()
        .then(() => {
          if (action === 'confirm') {
            openRepublishInfoDialog({
              onCloseComplete: () => {
                navigate(settingsRoutes.salePreset);
              },
            });
            return;
          }

          navigate(settingsRoutes.salePreset);
        })
        .catch(handleApiError);
    }
  };

  const openSetNewVehicleDialog = (
    values: SalePresetFormValues,
    canPropagateQualitySeal: boolean,
    qualitySealImpact?: GetSalePresetQualitySealImpactResponseBody
  ) => {
    openDialog(
      <SetNewVehicleDialog
        isLoading={isLoading}
        qualitySealImpact={qualitySealImpact}
        onCancel={() => {
          void createOrUpdateSalePreset(values, 'cancel', canPropagateQualitySeal);
        }}
        onConfirm={() => {
          void createOrUpdateSalePreset(values, 'confirm', canPropagateQualitySeal);
        }}
      />,
      {
        'data-testid': 'setVehicles-newSalePreset-dialog',
        id: 'salePreset-setVehiclesDialog-new',
        title: i18n.t('entity.salePreset.setNewVehicleDialog.title'),
      }
    );
  };

  const onSubmit = async (values: SalePresetFormValues) => {
    const qualitySealChanged =
      isQualitySealVisible &&
      values.defaultQualitySealKey !== (props.settings?.defaultQualitySealKey ?? null);

    if (id && qualitySealChanged) {
      await getQualitySealImpact({
        salePresetId: id,
        defaultQualitySealKey: values.defaultQualitySealKey,
      })
        .unwrap()
        .then((impact) => {
          openSetNewVehicleDialog(values, true, impact);
        })
        .catch(handleApiError);
      return;
    }

    openSetNewVehicleDialog(values, !id && isQualitySealVisible);
  };

  return (
    <Form<SalePresetFormValues>
      onSubmit={onSubmit}
      experimentalZodSchema={salePresetFormSchema}
      defaultValues={{
        name: props.settings?.name ?? '',
        makes: props.settings?.makes ?? [],
        watermarkId: props.settings?.watermarkId ?? null,
        backgroundRemovalId: props.settings?.backgroundRemovalId ?? null,
        digitalCertificateThemeId: props.settings?.digitalCertificateThemeId ?? null,
        warrantyProgramId: props.settings?.warrantyProgramId ?? null,
        defaultQualitySealKey: props.settings?.defaultQualitySealKey ?? null,
      }}
      isFullHeight
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <VStack spacing={4} maxWidth={177}>
            <FormField
              control={control}
              type="text"
              name="name"
              isRequired
              label={i18n.t('page.salesSettings.salePreset.labels.name')}
            />
            <HStack spacing={6}>
              <Box flex={0.5}>
                <MakeSelect
                  vehicleType="VEHICLETYPE_PASSENGER_CAR"
                  control={control}
                  name="makes"
                  label={i18n.t('page.salesSettings.salePreset.labels.makes')}
                  isMultiple
                  data-testid="salePreset-make-select"
                  onChange={(value) => onChangeMakes(value, formApi)}
                />
              </Box>
              <Space />
            </HStack>
            <Show when={isQualitySealVisible}>
              <QualitySealFormField control={control} settings={props.settings} />
            </Show>
            <HStack spacing={6}>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="choice"
                  name="watermarkId"
                  options={watermarkListOptions ?? []}
                  label={i18n.t('page.salesSettings.salePreset.labels.watermark')}
                />
              </Box>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="choice"
                  name="digitalCertificateThemeId"
                  options={themeListOptions ?? []}
                  label={i18n.t('page.salesSettings.salePreset.labels.digitalCertificate')}
                />
              </Box>
            </HStack>
            <HStack spacing={6}>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="choice"
                  name="backgroundRemovalId"
                  options={backgroundRemovalOptions ?? []}
                  label={i18n.t('page.salesSettings.salePreset.labels.backgroundRemoval')}
                />
              </Box>
              <Show when={isSalesWarrantyEnabled}>
                <Box flex={1}>
                  <FormField
                    control={control}
                    type="choice"
                    name="warrantyProgramId"
                    options={warrantyProgramOptions ?? []}
                    label={i18n.t('entity.warranty.labels.warrantyProgram')}
                  />
                </Box>
              </Show>
            </HStack>
          </VStack>
          <Separator spacing={2} />

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discardChanges')}
              variant="secondary"
              onClick={() => navigate(settingsRoutes.salePreset)}
              data-testid="salePreset-discardChangesBtn"
            />
            <FormButton
              title={i18n.t('general.actions.saveAndClose')}
              control={control}
              type="submit"
              data-testid="salePreset-submitBtn"
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
